
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Award, Calendar } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Candidate = Tables<'candidates'>;

interface CandidateExperienceInfoProps {
  candidate: Candidate;
}

// Helper function to render JSON array data as readable text
const renderArrayData = (data: any, title: string) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No {title.toLowerCase()} provided
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="p-3 bg-gray-50 rounded-lg border">
          <p className="text-sm text-gray-700">{item}</p>
        </div>
      ))}
    </div>
  );
};

export function CandidateExperienceInfo({ candidate }: CandidateExperienceInfoProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Experience & Activities
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Projects */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Projects
          </h4>
          {renderArrayData(candidate.projects_details, "Projects")}
        </div>

        {/* Project Links */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Project Links
          </h4>
          {renderArrayData(candidate.project_links, "Project Links")}
        </div>

        {/* Experience */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Internships & Experience
          </h4>
          {renderArrayData(candidate.internships_experience, "Experience")}
        </div>

        {/* Certifications & Awards */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Award className="h-4 w-4" />
            Certifications & Awards
          </h4>
          {renderArrayData(candidate.certifications_awards, "Certifications & Awards")}
        </div>

        {/* Event Participations */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Event Participations
          </h4>
          {renderArrayData(candidate.event_participations, "Event Participations")}
        </div>
      </CardContent>
    </Card>
  );
}
